import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../App';

const MESICE = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

const stavBadge = (stav) => {
  if (stav === 'schváleno') return { bg: '#d8f3dc', color: '#1b4332', text: '✓ Schváleno' };
  if (stav === 'zamítnuto') return { bg: '#fde8e6', color: '#c0392b', text: '✗ Zamítnuto' };
  return { bg: '#fef3c7', color: '#92400e', text: '⏳ Čeká na schválení' };
};

export default function MojeVykazy({ refreshKey }) {
  const { profile: user } = useAuth();
  const now = new Date();
  const [rok, setRok] = useState(now.getFullYear());
  const [mesic, setMesic] = useState(now.getMonth() + 1);
  const [vykazy, setVykazy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pullRefreshing, setPullRefreshing] = useState(false);
  const touchStartY = useRef(null);

  const fetchVykazy = useCallback(async () => {
    setLoading(true);
    const od = `${rok}-${String(mesic).padStart(2,'0')}-01`;
    const do_ = `${rok}-${String(mesic).padStart(2,'0')}-${new Date(rok, mesic, 0).getDate()}`;

    const { data } = await supabase
      .from('vykazy')
      .select('*, zakazky(nazev)')
      .eq('zamestnanec_id', user.id)
      .gte('datum', od)
      .lte('datum', do_)
      .order('datum', { ascending: false });

    setVykazy(data || []);
    setLoading(false);
  }, [rok, mesic, user.id]);

  useEffect(() => { fetchVykazy(); }, [fetchVykazy, refreshKey]);

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = async (e) => {
    if (touchStartY.current === null) return;
    const diff = e.changedTouches[0].clientY - touchStartY.current;
    if (diff > 80 && window.scrollY === 0) {
      setPullRefreshing(true);
      await fetchVykazy();
      setPullRefreshing(false);
    }
    touchStartY.current = null;
  };

  const schvaleneHodiny = vykazy.filter(v => v.stav === 'schváleno').reduce((s, v) => s + parseFloat(v.hodiny), 0);
  const celkemHodiny = vykazy.reduce((s, v) => s + parseFloat(v.hodiny), 0);

  return (
    <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {pullRefreshing && (
        <div style={{ textAlign: 'center', padding: '10px', color: '#2d6a4f', fontWeight: 600, fontSize: 14 }}>
          🔄 Aktualizuji…
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: 0 }}>Moje výkazy</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={mesic} onChange={e => setMesic(+e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'DM Sans, sans-serif', background: '#fff', outline: 'none' }}>
            {MESICE.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={rok} onChange={e => setRok(+e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'DM Sans, sans-serif', background: '#fff', outline: 'none' }}>
            {[2024,2025,2026].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Schválené hodiny', value: `${schvaleneHodiny} h`, color: '#2d6a4f' },
          { label: 'Celkem vykázáno', value: `${celkemHodiny} h`, color: '#111' },
          { label: 'Výkazů', value: vykazy.length, color: '#555' },
        ].map((c, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 12, padding: '12px', borderTop: `3px solid ${c.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: '#888' }}>Načítám…</div>
      ) : vykazy.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: '#888' }}>Žádné výkazy v tomto období</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {vykazy.map(v => {
            const badge = stavBadge(v.stav);
            return (
              <div key={v.id} style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: `4px solid ${badge.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 13, color: '#888' }}>📅 {v.datum}</div>
                  <div style={{ fontWeight: 800, fontSize: 20, color: '#2d6a4f' }}>{v.hodiny} h</div>
                </div>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#111', marginBottom: 6 }}>📋 {v.zakazky?.nazev}</div>
                {v.poznamka && <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>💬 {v.poznamka}</div>}
                <span style={{ display: 'inline-block', background: badge.bg, color: badge.color, padding: '5px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                  {badge.text}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
